import { useStatsigClient } from '@statsig/react-bindings';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { V1UserNotification } from '@/generated/hooks/herald/models';
import {
  EmptyState,
  Flex,
  Separator,
  Skeleton,
  Spinner,
} from '@kognitos/lattice';

import { ANALYTICS_EVENTS } from '@/shared/analytics/events';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { useNavigate } from 'react-router-dom';
import { NotificationCard } from './NotificationCard';

interface NotificationListProps {
  notifications: V1UserNotification[];
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  onMarkAsRead?: () => void;
}

export function NotificationList({
  notifications,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onClose,
  onMarkAsRead,
}: NotificationListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');
  const { logEvent } = useStatsigClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;

    if (isNearBottom) {
      onLoadMore();
    }
  }, [isFetchingNextPage, hasNextPage, onLoadMore]);
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const redirectToNotificationsSettings = () => {
    logEvent(ANALYTICS_EVENTS.NOTIFICATION_MANAGE_SETTINGS_CLICKED, '', {
      source: 'empty_state',
    });
    onClose();
    navigate(
      getRoutePath('WORKSPACE_SETTINGS_NOTIFICATIONS', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
      }),
    );
  };

  if (isLoading) {
    return (
      <Flex direction="column" gap={3} width="full" className="py-4">
        <Flex direction="column" gap={2} width="full">
          <Skeleton className="h-4 w-[150px]" />
          <Skeleton className="h-4 w-full" />
        </Flex>
        <Separator />
        <Flex direction="column" gap={2} width="full">
          <Skeleton className="h-4 w-[150px]" />
          <Skeleton className="h-4 w-full" />
        </Flex>
        <Separator />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex justify="center" align="center" width="full" className="py-8">
        <EmptyState
          icon="AlertCircle"
          description={t('notifications.center.error.title')}
        />
      </Flex>
    );
  }

  if (notifications.length === 0) {
    return (
      <Flex
        justify="center"
        align="center"
        width="full"
        className="flex-1 py-8 h-full"
      >
        <EmptyState
          icon="Bell"
          title={t('notifications.center.empty.title')}
          description={t('notifications.center.empty.description')}
          bordered
          secondaryActionLabel={t('notifications.center.manageNotifications')}
          onSecondaryAction={redirectToNotificationsSettings}
          className="!border-none"
        />
      </Flex>
    );
  }

  return (
    <Flex
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="flex-1 flex-col gap-3 overflow-y-auto w-full min-h-0"
    >
      {notifications.map(notification => (
        <NotificationCard
          key={notification.id}
          notification={notification}
          onClose={onClose}
          onMarkAsRead={onMarkAsRead}
        />
      ))}

      {isFetchingNextPage && (
        <Flex justify="center" className="py-4">
          <Spinner />
        </Flex>
      )}
    </Flex>
  );
}
